import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, finalize, map, Observable, switchMap, tap, withLatestFrom } from 'rxjs';
import { AppState } from '../../../../store/state/app.state';
import { AuthSelectors } from '../../../auth/store/selectors';
import { ServerErrorService } from '../../../shared/services/server-error/server-error.service';
import { UuidService } from '../../../shared/services/uuid/uuid.service';
import { LoadingActions } from '../../../shared/store/actions';
import { CommonUtils } from '../../../shared/utils/common-utils';
import { ActivityRecordVerificationStatus } from '../../data-model/enums/activity-record-verification-status.enum';
import { ActivityResponse, ActivityService } from '../../services/api/activity/activity.service';
import { ActivityActions, NavigationActions } from '../actions';
import { initialActivityState } from '../reducers/activity.reducer';
import { ActivitySelectors, ServiceLocationSelectors } from '../selectors';

@Injectable()
export class ActivityEffects {
  loadActivityRecords$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        ActivityActions.loadActivityRecords,
        ActivityActions.setLimit,
        ActivityActions.setOffset,
        ActivityActions.setVerificationStatus,
        ActivityActions.setFilter
      ),
      filter(action => !CommonUtils.isDefinedAndFalse(action.fetchActivityRecords)),
      withLatestFrom(
        this.store.select(ActivitySelectors.selectLimit),
        this.store.select(ActivitySelectors.selectOffset),
        this.store.select(ActivitySelectors.selectFilter),
        this.store.select(ActivitySelectors.selectVerificationStatus),
        this.store.select(ServiceLocationSelectors.selectServiceLocationId),
        this.store.select(AuthSelectors.selectUserIsSupervisor)
      ),
      switchMap(([action, limit, offset, filter, verificationStatus, serviceLocationId, userIsSupervisor]) => {
        const isFilterOrVerificationStatusChange = action.type === ActivityActions.setFilter.type || action.type === ActivityActions.setVerificationStatus.type;

        const adjustedOffset = isFilterOrVerificationStatusChange ? initialActivityState.offset : offset;
        const adjustedLimit = isFilterOrVerificationStatusChange ? initialActivityState.limit : limit;

        const loaderId = this.uuidService.generate();
        this.store.dispatch(LoadingActions.showLoadingIndicatorWithId({ message: 'Loading activity records...', loaderId }));

        let activityRecords$: Observable<ActivityResponse>;
        switch (verificationStatus) {
          case ActivityRecordVerificationStatus.ACTIVITY: {
            activityRecords$ = this.activityService.getActivityRecords(serviceLocationId, adjustedLimit, adjustedOffset, filter, userIsSupervisor);
            break;
          }
          case ActivityRecordVerificationStatus.MISSING: {
            activityRecords$ = this.activityService.getMissingItems(serviceLocationId, adjustedLimit, adjustedOffset, filter);
            break;
          }
          default: {
            throw new Error('invalid verification status. valid statuses are ACTIVITY and MISSING');
          }
        }

        return activityRecords$.pipe(
          tap(() => {
            if (isFilterOrVerificationStatusChange) {
              this.store.dispatch(ActivityActions.setOffset({ offset: adjustedOffset, fetchActivityRecords: false }));
              this.store.dispatch(ActivityActions.setLimit({ limit: adjustedLimit, fetchActivityRecords: false }));
            }
          }),
          map(response => ActivityActions.activityRecordsLoaded({ activityRecords: response.results, numberOfActivityRecords: response.total_results })),
          catchError(error => this.serverErrorService.handleError(error)),
          finalize(() => this.store.dispatch(LoadingActions.hideLoadingIndicatorWithId({ loaderId })))
        );
      })
    );
  });

  exportActivityRecords$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActivityActions.exportActivityRecords),
      withLatestFrom(
        this.store.select(ServiceLocationSelectors.selectServiceLocationId),
        this.store.select(ActivitySelectors.selectLimit),
        this.store.select(ActivitySelectors.selectOffset),
        this.store.select(ActivitySelectors.selectFilter),
        this.store.select(AuthSelectors.selectUserIsSupervisor)
      ),
      switchMap(([, serviceLocationId, limit, offset, filter, userIsSupervisor]) => {
        return this.activityService.getActivityRecordsCSV(serviceLocationId, limit, offset, filter, userIsSupervisor).pipe(
          tap(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'activity_records.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }),
          map(() => ActivityActions.activityRecordsExported()),
          catchError(error => this.serverErrorService.handleError(error))
        );
      })
    );
  });

  exportMissingActivityRecords$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActivityActions.exportMissingActivityRecords),
      withLatestFrom(
        this.store.select(ServiceLocationSelectors.selectServiceLocationId),
        this.store.select(ActivitySelectors.selectLimit),
        this.store.select(ActivitySelectors.selectOffset),
        this.store.select(ActivitySelectors.selectFilter)
      ),
      switchMap(([, serviceLocationId, limit, offset, filter]) => {
        return this.activityService.getMissingItemsCSV(serviceLocationId, limit, offset, filter).pipe(
          tap(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'missing_activity_records.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          }),
          map(() => ActivityActions.missingActivityRecordsExported()),
          catchError(error => this.serverErrorService.handleError(error))
        );
      })
    );
  });

  loadMissingActivityRecords$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActivityActions.loadMissingActivityRecords),
      concatLatestFrom(() => this.store.select(ServiceLocationSelectors.selectServiceLocationId)),
      switchMap(([, serviceLocationId]) =>
        this.activityService.getMissingItems(serviceLocationId, 0, 0, null).pipe(
          map(response => ActivityActions.missingActivityRecordsLoaded({ numberOfMissingActivityRecords: response.total_results })),
          catchError(error => this.serverErrorService.handleError(error))
        )
      )
    );
  });

  closeItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActivityActions.closeItem),
      switchMap(action => {
        const loaderId = this.uuidService.generate();
        this.store.dispatch(LoadingActions.showLoadingIndicatorWithId({ loaderId, message: 'Closing item...' }));
        if (CommonUtils.isNullOrUndefined(action.request.activity_id)) {
          return this.activityService.addActivityRecord(action.request).pipe(
            tap(() => this.store.dispatch(NavigationActions.navigateFromCloseEventToInProgressMyApprovedList())),
            map(() => ActivityActions.itemClosed()),
            catchError(error => this.serverErrorService.handleError(error)),
            finalize(() => this.store.dispatch(LoadingActions.hideLoadingIndicatorWithId({ loaderId })))
          );
        }
        return this.activityService.updateActivityRecord(action.request).pipe(
          tap(() => this.store.dispatch(NavigationActions.navigateFromDailyLogDetailToActivityLogSummaries())),
          map(() => ActivityActions.itemClosed()),
          catchError(error => this.serverErrorService.handleError(error)),
          finalize(() => this.store.dispatch(LoadingActions.hideLoadingIndicatorWithId({ loaderId })))
        );
      })
    );
  });

  deleteItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ActivityActions.deleteItem),
      switchMap(action => {
        const loaderId = this.uuidService.generate();
        this.store.dispatch(LoadingActions.showLoadingIndicatorWithId({ loaderId, message: 'Deleting item...' }));
        return this.activityService.deleteActivityRecord(action.activityId).pipe(
          tap(() => this.store.dispatch(NavigationActions.navigateFromDailyLogDetailToActivityLogSummaries())),
          map(() => ActivityActions.itemDeleted()),
          catchError(error => this.serverErrorService.handleError(error)),
          finalize(() => this.store.dispatch(LoadingActions.hideLoadingIndicatorWithId({ loaderId })))
        );
      })
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly serverErrorService: ServerErrorService,
    private readonly activityService: ActivityService,
    private readonly store: Store<AppState>,
    private readonly uuidService: UuidService
  ) {}
}
